import { faTag } from '@fortawesome/pro-light-svg-icons/faTag';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { FunctionComponent, MouseEvent, useState } from 'react';
import styles from './PetParent.module.scss';
import { Popup } from '../UI/ListPetInfo/Popup/Popup';
import PetParentTag from './PetParentTag';
import { useDispatch, useSelector } from 'react-redux';
import { ClientsStatus, getClientTagsThunk } from '@spike/clients-action';
import { useApiClientWrapper } from 'hooks/useApiClientWrapper';
import { RootState } from 'store';
import { ClientTags } from '@spike/client-model';
import { Spinner } from 'components/UI/V3/Spinner/Spinner';
import useNonInitialEffect from '@versiondos/hooks';
import { CustomerListItem } from '@spike/appointments-list-model';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import { CardOnFileIndicator } from './CardOnFile/CardOnFileIndicator';
import { CardOnFile } from '@spike/payments-model';
interface PetParentProps {
    customer: CustomerListItem;
    marketplaceId: number;
    showOnly?: boolean;
    showTagsIcon?: boolean;
    className?: string;
    onClick?: () => void;
}

export const PetParent: FunctionComponent<PetParentProps> = props => {
    const apiClientWrapper = useApiClientWrapper();
    const dispatch = useDispatch();

    const tags = useSelector<RootState, ClientTags>(state => state.clients.tags ?? { positive: [], negative: [] });
    const clientStatus = useSelector<RootState, ClientsStatus>(state => state.clients.status);
    const cardsOnFile = useSelector<RootState, CardOnFile[]>(
        state => state.payments.cardsOnFileByMarketplaceAndCustomer[props.marketplaceId]?.[props.customer.id] ?? []
    );
    const isLoadingCardsOnFile = useSelector<RootState, boolean>(
        state => state.payments.cardsOnFileLoadingByMarketplace[props.marketplaceId] ?? false
    );

    const [loading, setLoading] = useState(false);

    useNonInitialEffect(() => {
        switch (clientStatus) {
            case ClientsStatus.GetTagsSuccess:
            case ClientsStatus.Error:
                setLoading(false);
                break;
        }
    }, [clientStatus]);

    const clickHandler = (event: MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        !props.showOnly && props.onClick?.();
    };

    const tagsClickHandler = () => {
        setLoading(true);
        dispatch(getClientTagsThunk(apiClientWrapper, props.customer.id));
    };

    const tagsList = (
        <div className={styles.tagsListContainer}>
            <span className={styles.tagsTitle}>Client Tags</span>
            <OverlayScrollbarsComponent
                options={{
                    scrollbars: { autoHide: 'never' },
                    overflowBehavior: { x: 'hidden', y: 'scroll' }
                }}
                style={{ height: '100%', width: '100%', paddingRight: 11 }}
            >
                {loading ? (
                    <div className={styles.spinnerContainer}>
                        <Spinner size={20} />
                    </div>
                ) : (
                    <>
                        {tags.positive.length === 0 && tags.negative.length === 0 && (
                            <span className={styles.noTags}>The client has not been tagged.</span>
                        )}
                        {tags.positive.length > 0 && (
                            <>
                                <>👍</>
                                <div className={styles.tagsRow}>
                                    {tags.positive.map(tag => (
                                        <PetParentTag
                                            key={tag.id}
                                            name={tag.name}
                                        />
                                    ))}
                                </div>
                            </>
                        )}
                        {tags.negative.length > 0 && (
                            <>
                                <>👎</>
                                <div className={styles.tagsRow}>
                                    {tags.negative.map(tag => (
                                        <PetParentTag
                                            key={tag.id}
                                            name={tag.name}
                                        />
                                    ))}
                                </div>
                            </>
                        )}
                    </>
                )}
            </OverlayScrollbarsComponent>
        </div>
    );

    return (
        <div className={clsx(styles.container, props.className)}>
            <div
                className={clsx(styles.petParentContainer, { [styles.showOnly]: props.showOnly })}
                onClick={clickHandler}
            >
                {`${props.customer.firstName} ${props.customer.lastName}`}
            </div>
            <CardOnFileIndicator
                cards={cardsOnFile}
                isLoading={isLoadingCardsOnFile}
            />
            {props.showTagsIcon && props.customer.hasTags && (
                <Popup
                    content={tagsList}
                    onClick={tagsClickHandler}
                >
                    <div className={styles.tagIconContainer}>
                        <FontAwesomeIcon
                            icon={faTag}
                            className={styles.tagIcon}
                        />
                    </div>
                </Popup>
            )}
        </div>
    );
};

export default PetParent;
